import React, { useEffect, useMemo, useRef, useState } from "react";
import "./VisaManage.css";
import jsPDF from "jspdf";
import Swal from "sweetalert2";
import { FiCheck, FiX } from "react-icons/fi";

const API = process.env.REACT_APP_API_URL || "http://localhost:5000";

/* tiny number animation */
function CountUp({ to = 0, duration = 900, prefix = "" }) {
  const [val, setVal] = useState(0);
  const start = useRef(null);
  const target = Number(to || 0);

  useEffect(() => {
    start.current = null;
    let raf = 0;
    const step = (ts) => {
      if (!start.current) start.current = ts;
      const p = Math.min(1, (ts - start.current) / duration);
      const eased = 1 - Math.pow(1 - p, 3);
      setVal(target * eased);
      if (p < 1) raf = requestAnimationFrame(step);
    };
    raf = requestAnimationFrame(step);
    return () => cancelAnimationFrame(raf);
  }, [target, duration]);

  return <>{prefix}{Math.round(val).toLocaleString()}</>;
}

function fmtDate(d) {
  if (!d) return "—";
  const dt = new Date(d);
  return dt.toLocaleDateString(undefined, {
    year: "numeric",
    month: "short",
    day: "2-digit",
  });
}

/* clickable badge — kept for compatibility but not used for status change */
function StatusBadge({ status }) {
  const key = String(status || "").toLowerCase();
  const map = {
    approved: "badge green",
    pending: "badge amber",
    rejected: "badge red",
  };
  const cls = map[key] || "badge gray";
  const label =
    key === "approved" ? "Approved" :
      key === "rejected" ? "Rejected" :
        (String(status) || "Pending");
  return <span className={cls}>{label}</span>;
}

export default function VisaManage() {
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [selectedApplication, setSelectedApplication] = useState(null);
  const [detailsLoading, setDetailsLoading] = useState(false);
  const [detailsError, setDetailsError] = useState("");
  const [editing, setEditing] = useState(null); // { applicationId, field, original }
  const [editValue, setEditValue] = useState("");
  const [savingField, setSavingField] = useState(false);

  const [summary, setSummary] = useState({
    approvedVisas: 0,
    pendingVisas: 0,
    approvedInsurances: 0,
    pendingInsurances: 0,
    deletedVisas: 0,
  });

  const [rows, setRows] = useState([]);
  const [search, setSearch] = useState("");
  const [showSearch, setShowSearch] = useState(false);

  const cards = useMemo(
    () => [
      { title: "Available Approval Visa", value: summary.approvedVisas, sub: "Approved Visa Applications", icon: "✅", prefix: "+" },
      { title: "Pending Visa", value: summary.pendingVisas, sub: "Pending Visa Applications", icon: "⏳", prefix: "+" },
      { title: "Available Approval Insurance", value: summary.approvedInsurances, sub: "Approved Insurances", icon: "🛡️", prefix: "+" },
      { title: "Pending Insurance", value: summary.pendingInsurances, sub: "Pending Insurances", icon: "🛡️", prefix: "+" },
      { title: "Deleted Visas", value: summary.deletedVisas, sub: "Soft-deleted visas", icon: "🗑️", prefix: "+" },
    ],
    [summary]
  );

  useEffect(() => {
    let mounted = true;
    async function load() {
      try {
        setLoading(true);
        setErr("");

        const [sumRes, listRes] = await Promise.all([
          fetch(`${API}/admin/visas/summary`),
          fetch(`${API}/admin/visas`),
        ]);

        const sum = await sumRes.json();
        const list = await listRes.json();

        if (!sumRes.ok) throw new Error(sum?.message || "Failed to load summary.");
        if (!listRes.ok) throw new Error(list?.message || "Failed to load visa list.");

        if (!mounted) return;

        setSummary({
          approvedVisas: Number(sum.approvedVisas) || 0,
          pendingVisas: Number(sum.pendingVisas) || 0,
          approvedInsurances: Number(sum.approvedInsurances) || 0,
          pendingInsurances: Number(sum.pendingInsurances) || 0,
          deletedVisas: Number(sum.deletedVisas) || 0,
        });
        setRows(Array.isArray(list) ? list : []);
      } catch (e) {
        if (mounted) setErr(e.message || "Load failed");
      } finally {
        if (mounted) setLoading(false);
      }
    }
    load();
    return () => { mounted = false; };
  }, []);

  // new: changeStatus accepts explicit requested status ("Approved" | "Rejected")
  async function changeStatus(applicationId, requestedStatus) {
    try {
      // optimistic update on the specific row
      setRows((prev) =>
        prev.map((r) =>
          r.applicationId === applicationId ? { ...r, status: requestedStatus } : r
        )
      );

      const res = await fetch(`${API}/admin/visas/${applicationId}/status`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ status: requestedStatus }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data?.message || "Failed to update visa status.");

      if (data.row) {
        setRows((prev) =>
          prev.map((r) => (r.applicationId === applicationId ? { ...r, ...data.row } : r))
        );
      }
      if (data.summary) {
        setSummary({
          approvedVisas: Number(data.summary.approvedVisas) || 0,
          pendingVisas: Number(data.summary.pendingVisas) || 0,
          approvedInsurances: Number(data.summary.approvedInsurances) || 0,
          pendingInsurances: Number(data.summary.pendingInsurances) || 0,
          deletedVisas: Number(data.summary.deletedVisas) || 0,
        });
      }

      Swal.fire({
        title: "Visa status updated",
        text: `Status set to "${requestedStatus}". An email has been sent to the applicant.`,
        icon: "success",
        timer: 1200,
        showConfirmButton: false,
      });

      // re-open details after a short delay
      setTimeout(() => {
        fetchApplicationDetails(applicationId);
      }, 800);
    } catch (e) {
      alert(e.message || "Update failed.");
      try {
        const listRes = await fetch(`${API}/admin/visas`);
        const list = await listRes.json();
        if (listRes.ok) setRows(Array.isArray(list) ? list : []);
      } catch { }
    }
  }

  async function fetchApplicationDetails(applicationId) {
    try {
      setDetailsLoading(true);
      setDetailsError("");
      const res = await fetch(`${API}/admin/visas/${applicationId}/details`);
      const data = await res.json();
      if (!res.ok) throw new Error(data?.message || "Failed to load details");
      setSelectedApplication(data);
    } catch (e) {
      setDetailsError(e.message || "Failed to load details");
    } finally {
      setDetailsLoading(false);
    }
  }

  function downloadPDF(app) {
    if (!app) return;
    const doc = new jsPDF();
    let y = 10;

    const addSection = (title, obj) => {
      if (!obj) return;
      doc.setFontSize(14);
      doc.setFont("helvetica", "bold");
      doc.text(title, 10, y);
      y += 8;
      doc.setFontSize(12);
      doc.setFont("helvetica", "normal");
      Object.entries(obj).forEach(([key, value]) => {
        if (value && typeof value === "object") {
          Object.entries(value).forEach(([subKey, subVal]) => {
            doc.setTextColor(75, 85, 99);
            doc.text(`${subKey}:`, 12, y);
            doc.setTextColor(17, 23, 42);
            doc.text(`${subVal || "—"}`, 60, y);
            y += 6;
          });
        } else {
          doc.setTextColor(75, 85, 99);
          doc.text(`${key}:`, 12, y);
          doc.setTextColor(17, 23, 42);
          doc.text(`${value || "—"}`, 60, y);
          y += 6;
        }
      });
      y += 4;
      if (y > 270) { doc.addPage(); y = 10; }
    };

    doc.setFontSize(16);
    doc.setFont("helvetica", "bold");
    doc.text("Visa Application Details", 10, y); y += 10;

    addSection("Application Summary", app.applicationSummary);
    addSection("User Information", app.userInformation);
    addSection("Passport Details", app.passportDetails);
    addSection("Travel Information", app.travelInformation);
    addSection("Contact Information", app.contactInformation);
    addSection("Payment Details", app.paymentDetails);

    doc.save(`${app.applicationSummary?.applicationId || "visa_application"}.pdf`);
  }

  function startEdit(applicationId, field, currentValue = "", target = "visa", applicantId = null) {
    setEditing({ applicationId, field, original: currentValue, target, applicantId });
    setEditValue(currentValue || "");
  }

  function cancelEdit() {
    setEditing(null);
    setEditValue("");
    setSavingField(false);
  }

  async function saveField(applicationId, field) {
    try {
      setSavingField(true);
      const res = await fetch(`${API}/admin/visas/${applicationId}/field`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ field, value: editValue }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data?.message || "Failed to save field.");

      // update list: replace row with returned row if present
      if (data.row) {
        setRows((prev) => prev.map((r) => (r.applicationId === data.row.applicationId ? { ...r, ...data.row } : r)));
      } else {
        // fallback: update local value
        setRows((prev) => prev.map((r) => (r.applicationId === applicationId ? { ...r, [field === "travel_purpose" ? "purpose" : field]: editValue } : r)));
      }

      Swal.fire({ title: "Saved", text: "Details Updated Successfully!", timer: 900, icon: "success", showConfirmButton: false });
      cancelEdit();
    } catch (e) {
      console.error(e);
      alert(e.message || "Save failed");
      setSavingField(false);
    }
  }

  // NEW: save applicant field
  async function saveApplicantField(applicantId, field) {
    try {
      setSavingField(true);
      const res = await fetch(`${API}/admin/visas/applicant/${applicantId}/field`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ field, value: editValue }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data?.message || "Failed to save applicant field.");

      if (data.applicant) {
        const a = data.applicant;
        setRows((prev) =>
          prev.map((r) => {
            if (r.applicantId !== a.id) return r;
            // update fields used in table
            return {
              ...r,
              userName: a.full_name || r.userName,
              passportNumber: a.passport_number || r.passportNumber,
              contact: {
                phone: a.phone_number || (r.contact && r.contact.phone) || null,
                email: a.email || (r.contact && r.contact.email) || null,
              },
              dob: a.dob || r.dob,
            };
          })
        );
      }

      Swal.fire({ title: "Saved", text: "Details Updated Successfully!", timer: 900, icon: "success", showConfirmButton: false });
      cancelEdit();
    } catch (e) {
      console.error(e);
      alert(e.message || "Save failed");
      setSavingField(false);
    }
  }

  const filteredRows = useMemo(() => {
    if (!search.trim()) return rows;
    return rows.filter((r) =>
      r.userName.toLowerCase().includes(search.trim().toLowerCase())
    );
  }, [search, rows]);

  if (loading) {
    return (
      <div className="vm-page">
        <div className="vm-head">
          <div>
            <h2 className="vm-title">Visa Management</h2>
            <p className="muted">Loading…</p>
          </div>
        </div>

        <section className="vm-cards">
          {[...Array(5)].map((_, i) => (
            <div key={i} className="card stat" style={{ opacity: 0.6 }}>
              <div className="stat-top">
                <div className="stat-title">Loading…</div>
                <div className="lock" aria-hidden>…</div>
              </div>
              <div className="stat-value">—</div>
              <div className="stat-sub">—</div>
            </div>
          ))}
        </section>

        <section className="card table-card" style={{ padding: 24, color: "gray" }}>
          Loading Visa Applications…
        </section>
      </div>
    );
  }

  if (err) {
    return <div className="vm-page" style={{ color: "crimson" }}>{err}</div>;
  }

  return (
    <div className="vm-page">
      <div className="vm-head">
        <h2 className="vm-title">Visa Management</h2>
        <p className="muted">Track and support user visa applications.</p>
      </div>

      <section className="vm-cards">
        {cards.map((s, i) => (
          <div key={i} className="card stat">
            <div className="stat-top">
              <div className="stat-title">{s.title}</div>
              <div className="lock" aria-hidden>{s.icon}</div>
            </div>
            <div className="stat-value">
              {typeof s.value === "number" ? <CountUp to={s.value} prefix={s.prefix || ""} /> : s.value}
            </div>
            <div className="stat-sub">{s.sub}</div>
          </div>
        ))}
      </section>

      <div className="vm-toolbar">
        <h3 className="table-title" style={{ flex: 1 }}>Visa Application List</h3>
        <button className="btn ghost" onClick={() => setShowSearch((s) => !s)}>
          {showSearch ? "Hide Filter" : "Filter by Name"}
        </button>
        {showSearch && (
          <input
            type="text"
            placeholder="Type user name..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            style={{ padding: "10px", borderRadius: 8, marginLeft: 12, marginTop: 15, border: "none" }}
          />
        )}
      </div>

      <section className="card table-card">
        {filteredRows.length === 0 ? (
          <div style={{ padding: 24, color: "#6b7280" }}>No visa applications submitted yet</div>
        ) : (
          <table className="table">
            <thead>
              <tr>
                <th><input type="checkbox" /></th>
                <th>Application ID</th>
                <th>User Name</th>
                <th>Nationality</th>
                <th>Passport Number</th>
                <th>Issue Date</th>
                <th>Expiry Date</th>
                <th>Contact info</th>
                <th>Date of Birth</th>
                <th>Purpose</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredRows.map((r) => (
                <tr key={r.applicationId} style={{ cursor: "pointer" }}>
                  <td><input type="checkbox" /></td>
                  <td><span className="id-badge">{r.appId}</span></td>
                  <td>
                    {editing && editing.applicationId === r.applicationId && editing.target === "applicant" && editing.field === "full_name" ? (
                      <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                        <input
                          value={editValue}
                          onChange={(e) => setEditValue(e.target.value)}
                          placeholder="Enter full name"
                          style={{ padding: 4, minWidth: 140, borderRadius: 6 }}
                          disabled={savingField}
                        />
                        <button className="btn approve" onClick={() => saveApplicantField(r.applicantId, "full_name")} disabled={savingField}><FiCheck /></button>
                        <button className="btn reject" onClick={cancelEdit} disabled={savingField}><FiX /></button>
                      </div>
                    ) : (
                      r.userName ? (
                        <span>{r.userName}</span>
                      ) : (
                        <em className="muted clickable" onClick={() => startEdit(r.applicationId, "full_name", r.userName || "", "applicant", r.applicantId)} title="Click to add username">Click to add</em>
                      )
                    )}
                  </td>
                  <td className="muted">{r.nationality}</td>
                  <td className="muted">
                    {editing && editing.applicationId === r.applicationId && editing.target === "applicant" && editing.field === "passport_number" ? (
                      <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                        <input
                          value={editValue}
                          onChange={(e) => setEditValue(e.target.value)}
                          placeholder="Enter passport number"
                          style={{ padding: 4, minWidth: 140, borderRadius: 6 }}
                          disabled={savingField}
                        />
                        <button className="btn approve" onClick={() => saveApplicantField(r.applicantId, "passport_number")} disabled={savingField}><FiCheck /></button>
                        <button className="btn reject" onClick={cancelEdit} disabled={savingField}><FiX /></button>
                      </div>
                    ) : (
                      r.passportNumber ? (
                        <span>{r.passportNumber}</span>
                      ) : (
                        <em className="muted clickable" onClick={() => startEdit(r.applicationId, "passport_number", r.passportNumber || "", "applicant", r.applicantId)} title="Click to add passport number">Click to add</em>
                      )
                    )}
                  </td>
                  <td className="muted">
                    {editing && editing.applicationId === r.applicationId && editing.target === "applicant" && editing.field === "passport_issue_date" ? (
                      <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                        <input
                          type="date"
                          value={editValue}
                          onChange={(e) => setEditValue(e.target.value)}
                          placeholder="Enter passport issue date"
                          style={{ padding: 4, minWidth: 140, borderRadius: 6 }}
                          disabled={savingField}
                        />
                        <button className="btn approve" onClick={() => saveApplicantField(r.applicantId, "passport_issue_date")} disabled={savingField}><FiCheck /></button>
                        <button className="btn reject" onClick={cancelEdit} disabled={savingField}><FiX /></button>
                      </div>
                    ) : (
                      r.issueDate ? (
                        <span>{fmtDate(r.issueDate)}</span>
                      ) : (
                        <em className="muted clickable" onClick={() => startEdit(r.applicationId, "passport_issue_date", r.issueDate || "", "applicant", r.applicantId)} title="Click to add passport issue date">Click to add</em>
                      )
                    )}
                  </td>
                  <td className="muted">{fmtDate(r.expiryDate)}</td>
                  <td className="muted">
                    <div className="mono">
                      {editing && editing.applicationId === r.applicationId && editing.target === "applicant" && editing.field === "phone_number" ? (
                        <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                          <input
                            value={editValue}
                            onChange={(e) => setEditValue(e.target.value)}
                            placeholder="Enter phone number"
                            style={{ padding: 4, minWidth: 140, borderRadius: 6 }}
                            disabled={savingField}
                          />
                          <button className="btn approve" onClick={() => saveApplicantField(r.applicantId, "phone_number")} disabled={savingField}><FiCheck /></button>
                          <button className="btn reject" onClick={cancelEdit} disabled={savingField}><FiX /></button>
                        </div>
                      ) : (
                        r.contact?.phone ? (
                          <span>{r.contact?.phone}</span>
                        ) : (
                          <em className="muted clickable" onClick={() => startEdit(r.applicationId, "phone_number", r.contact?.phone || "", "applicant", r.applicantId)} title="Click to add phone number">Click to add</em>
                        )
                      )}
                    </div>
                    <div className="mono">
                      {editing && editing.applicationId === r.applicationId && editing.target === "applicant" && editing.field === "email" ? (
                        <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                          <input
                            value={editValue}
                            onChange={(e) => setEditValue(e.target.value)}
                            placeholder="Enter email"
                            style={{ padding: 4, minWidth: 140, borderRadius: 6 }}
                            disabled={savingField}
                          />
                          <button className="btn approve" onClick={() => saveApplicantField(r.applicantId, "email")} disabled={savingField}><FiCheck /></button>
                          <button className="btn reject" onClick={cancelEdit} disabled={savingField}><FiX /></button>
                        </div>
                      ) : (
                        r.contact?.email ? (
                          <span>{r.contact?.email}</span>
                        ) : (
                          <em className="muted clickable" onClick={() => startEdit(r.applicationId, "email", r.contact?.email || "", "applicant", r.applicantId)} title="Click to add email">Click to add</em>
                        )
                      )}
                    </div>
                  </td>
                  <td>
                    {editing && editing.applicationId === r.applicationId && editing.target === "applicant" && editing.field === "dob" ? (
                      <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                        <input
                          type="date"
                          value={editValue}
                          onChange={(e) => setEditValue(e.target.value)}
                          placeholder="Enter Date of Birth"
                          style={{ padding: 4, minWidth: 140, borderRadius: 6 }}
                          disabled={savingField}
                        />
                        <button className="btn approve" onClick={() => saveApplicantField(r.applicantId, "dob")} disabled={savingField}><FiCheck /></button>
                        <button className="btn reject" onClick={cancelEdit} disabled={savingField}><FiX /></button>
                      </div>
                    ) : (
                      r.dob ? (
                        <span>{fmtDate(r.dob)}</span>
                      ) : (
                        <em className="muted clickable" onClick={() => startEdit(r.applicationId, "dob", r.dob || "", "applicant", r.applicantId)} title="Click to add date of birth">Click to add</em>
                      )
                    )}
                  </td>
                  <td>
                    {editing && editing.applicationId === r.applicationId && editing.field === "travel_purpose" ? (
                      <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                        <input
                          value={editValue}
                          onChange={(e) => setEditValue(e.target.value)}
                          placeholder="Enter purpose"
                          style={{ padding: 4, minWidth: 160, borderRadius: 6 }}
                          disabled={savingField}
                        />
                        <button className="btn approve" onClick={() => saveField(r.applicationId, "travel_purpose")} disabled={savingField}>
                          <FiCheck />
                        </button>
                        <button className="btn reject" onClick={cancelEdit} disabled={savingField}>
                          <FiX />
                        </button>
                      </div>
                    ) : (
                      <>
                        {r.purpose ? (
                          <span className="badge blue">{r.purpose}</span>
                        ) : (
                          <em
                            className="muted clickable"
                            onClick={() => startEdit(r.applicationId, "travel_purpose", r.purpose || "")}
                            title="Click to add purpose"
                            style={{ color: "#6b7280" }}
                          >
                            Click to add
                          </em>
                        )}
                      </>
                    )}
                  </td>
                  <td>
                    <StatusBadge status={r.status} />
                  </td>
                  <td style={{ display: "flex", gap: 8 }}>
                    {/* Approve button: disabled when already Approved */}
                    <button
                      className="btn approve"
                      onClick={() => changeStatus(r.applicationId, "Approved")}
                      disabled={String(r.status).toLowerCase() === "approved"}
                      title="Approve application"
                    >
                      <FiCheck style={{ marginRight: 8 }} />
                    </button>

                    {/* Reject button: disabled when already Rejected */}
                    <button
                      className="btn reject"
                      onClick={() => changeStatus(r.applicationId, "Rejected")}
                      disabled={String(r.status).toLowerCase() === "rejected"}
                      title="Reject application"
                    >
                      <FiX style={{ marginRight: 8 }} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
        {selectedApplication && (
          <div className="modal-backdrop">
            <div className="modal" style={{ maxWidth: "800px" }}>
              <div className="modal-content">
                <h2>Visa Application Details</h2>

                {detailsLoading ? (
                  <p>Loading…</p>
                ) : detailsError ? (
                  <p style={{ color: "crimson" }}>{detailsError}</p>
                ) : (
                  <>
                    {/* Application Summary */}
                    <div className="modal-section">
                      <h3>Application Summary</h3>
                      <div className="field"><span className="label">Application ID</span><span className="value">{selectedApplication.applicationSummary.applicationId || "N/A"}</span></div>
                      <div className="field"><span className="label">Submission Date</span><span className="value">{fmtDate(selectedApplication.applicationSummary.submissionDate) || "N/A"}</span></div>
                      <div className="field"><span className="label">Status</span><span className="value">{selectedApplication.applicationSummary.status || "N/A"}</span></div>
                      <div className="field"><span className="label">Processed On</span><span className="value">{fmtDate(selectedApplication.applicationSummary.processedOn) || "N/A"}</span></div>
                    </div>

                    {/* User Information */}
                    <div className="modal-section">
                      <h3>User Information</h3>
                      <div className="field"><span className="label">Full Name</span><span className="value">{selectedApplication.userInformation.firstName || "N/A"}</span></div>
                      <div className="field"><span className="label">Date of Birth</span><span className="value">{fmtDate(selectedApplication.userInformation.dob) || "N/A"}</span></div>
                      <div className="field"><span className="label">Nationality</span><span className="value">{selectedApplication.userInformation.nationality || "N/A"}</span></div>
                      <div className="field"><span className="label">Gender</span><span className="value">{selectedApplication.userInformation.gender}</span></div>
                    </div>

                    {/* Passport Details */}
                    <div className="modal-section">
                      <h3>Passport Details</h3>
                      <div className="field"><span className="label">Passport Number</span><span className="value">{selectedApplication.passportDetails.passportNumber || "N/A"}</span></div>
                      <div className="field"><span className="label">Issue Date</span><span className="value">{fmtDate(selectedApplication.passportDetails.issueDate) || "N/A"}</span></div>
                      <div className="field"><span className="label">Expiry Date</span><span className="value">{fmtDate(selectedApplication.passportDetails.expiryDate) || "N/A"}</span></div>
                    </div>

                    {/* Travel Information */}
                    <div className="modal-section">
                      <h3>Travel Information</h3>
                      <div className="field"><span className="label">Purpose of Visit</span><span className="value">{selectedApplication.travelInformation.purpose || "N/A"}</span></div>
                      <div className="field"><span className="label">Arrival Date</span><span className="value">{fmtDate(selectedApplication.travelInformation.arrivalDate) || "N/A"}</span></div>
                      <div className="field"><span className="label">Departure Date</span><span className="value">{fmtDate(selectedApplication.travelInformation.departureDate) || "N/A"}</span></div>
                      <div className="field"><span className="label">Accommodation</span><span className="value">{selectedApplication.travelInformation.accommodation || "N/A"}</span></div>
                    </div>

                    {/* Contact Information */}
                    <div className="modal-section">
                      <h3>Contact Information</h3>
                      <div className="field"><span className="label">Email</span><span className="value">{selectedApplication.contactInformation.email || "N/A"}</span></div>
                      <div className="field"><span className="label">Phone Number</span><span className="value">{selectedApplication.contactInformation.phone || "N/A"}</span></div>
                      <div className="field"><span className="label">Emergency Contact</span><span className="value">{selectedApplication.contactInformation.emergencyContact.name || "Not provided"} / {selectedApplication.contactInformation.emergencyContact.phone || "—"}</span></div>
                    </div>

                    {/* Payment Details */}
                    <div className="modal-section">
                      <h3>Payment Details</h3>
                      <div className="field"><span className="label">Service Fee</span><span className="value">{selectedApplication.paymentDetails.serviceFee || "N/A"}</span></div>
                      <div className="field"><span className="label">Payment Method</span><span className="value">{selectedApplication.paymentDetails.paymentMethod || "N/A"}</span></div>
                      <div className="field"><span className="label">Transaction ID</span><span className="value">{selectedApplication.paymentDetails.transactionId || "N/A"}</span></div>
                      <div className="field"><span className="label">Payment Status</span><span className="value">{selectedApplication.paymentDetails.paymentStatus || "N/A"}</span></div>
                    </div>

                    {/* Buttons */}
                    <div className="modal-actions">
                      <button className="btn delete" onClick={() => downloadPDF(selectedApplication)}>Download PDF</button>
                      <button className="btn cancel" onClick={() => setSelectedApplication(null)}>Cancel</button>
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>
        )}
      </section>
    </div>
  );
}